import { CheckCircleIcon, ChevronRightIcon } from "@chakra-ui/icons";
import {
	Box,
	Breadcrumb,
	BreadcrumbItem,
	BreadcrumbLink,
	Flex,
	Step,
	StepIndicator,
	Stepper,
	StepSeparator,
	Tab,
	TabList,
	TabPanel,
	TabPanels,
	Text,
} from "@chakra-ui/react";
import { format } from "date-fns";
import { type ReactNode, useCallback, useMemo } from "react";
import { NavLink, useParams } from "react-router-dom";
import Chat from "../components/Chat";
import CommandStatusBadge from "../components/CommandStatusBadge";
import Document from "../components/Document";
import InlineUserAvatar from "../components/InlineUserAvatar";
import PageTitle from "../components/PageTitle";
import PersistingTabIndex from "../components/PersistingTabIndex";
import TicketPriorityBadge from "../components/TicketPriorityBadge";
import TicketStatusBadge from "../components/TicketStatusBadge";
import { useCommandActivityLogsPaginated } from "../hooks/commands.hooks";
import {
	useTicket,
	useTicketDocuments,
	useTicketLogs,
} from "../hooks/tickets.hooks";
import { useConnectedUser } from "../hooks/user.hooks";
import i18n from "../i18n";
import {
	TicketStatus,
	type TicketLog,
	type TicketLogChangeStatusMeta,
} from "../types/ticket.types";
import type {
	ActivityLog,
	ActivityLogChangeStatusMeta,
} from "../types/logs.types";
import { getTicketPatient } from "./ticketingDetailPage.utils";

const CommandLastStatusChangeAuthor = ({
	id,
}: {
	id: number | string | null;
}) => {
	const logs = useCommandActivityLogsPaginated({ id: id ?? undefined })(
		1,
		1,
		undefined,
		{
			type: "CHANGE_STATUS,UPDATE,",
		},
		["-createdAt"],
	);

	if (logs?.data?.length === 0) {
		return null;
	}
	return (
		<Text fontSize={"14px"} textTransform={"capitalize"}>
			{logs?.data?.[0]?.user?.firstName} {logs?.data?.[0]?.user?.lastName}
		</Text>
	);
};

function TicketingDetailLogs({
	id,
	commandId,
}: {
	id: number | string;
	commandId?: number | string;
}) {
	const ticketLogs = useTicketLogs(id);
	const commandLogs = useCommandActivityLogsPaginated({ id: commandId })(
		1,
		1000,
		undefined,
		{
			type: "CHANGE_STATUS",
		},
		["-createdAt"],
	);
	const getTicketLogTitle = useCallback((log: TicketLog) => {
		const ticketLogsTypeToTitle = new Map<
			string,
			(log: TicketLog) => string | ReactNode
		>([
			["CREATED", () => "Création"],
			[
				"CHANGE_STATUS",
				(log: TicketLog) => {
					const meta = log.meta as TicketLogChangeStatusMeta;
					return <Text>{i18n.t(`ticket.status.${meta.to}`)}</Text>;
				},
			],
		]);
		const title = ticketLogsTypeToTitle.get(log.type);
		if (title) {
			return title(log);
		}
		return "";
	}, []);

	const getActivityLogTitle = useCallback((log: ActivityLog) => {
		const ticketLogsTypeToTitle = new Map<
			string,
			(log: ActivityLog) => string | ReactNode
		>([
			["CREATED", () => "Création"],
			[
				"CHANGE_STATUS",
				(log: ActivityLog) => {
					const meta = log.meta as ActivityLogChangeStatusMeta;
					return (
						<Text>{i18n.t(`command.status.${meta.next ?? meta.to}`)}</Text>
					);
				},
			],
		]);
		const title = ticketLogsTypeToTitle.get(log.type);
		if (title) {
			return title(log);
		}
		return "";
	}, []);

	const getDate = useCallback((log: TicketLog) => {
		return format(log.createdAt, "dd MMMM yyyy HH:mm");
	}, []);

	const getTicketLogDescription = useCallback((log: TicketLog) => {
		if (log.type === "CHANGE_STATUS") {
			const meta = log.meta as TicketLogChangeStatusMeta;
			const discount = meta?.discount ?? undefined;
			if (discount) {
				return (
					<Flex direction="row" gap={2} alignItems="center">
						<CheckCircleIcon color="#15803D" />
						<Text
							color="#15803D"
							lineHeight="21px"
							fontSize="15px"
							fontWeight="400"
						>
							Remise de {discount}% sur la commande
						</Text>
					</Flex>
				);
			}
		}
		return null;
	}, []);

	const logs = useMemo(() => {
		const ticketLogsArray = ticketLogs ?? [];
		const commandLogsArray = commandLogs?.data ?? [];
		return [
			...ticketLogsArray.map((log) => ({
				id: `ticket-${log.id}`,
				title: getTicketLogTitle(log),
				description: getTicketLogDescription(log),
				date: getDate(log),
				createdAt: log.createdAt,
			})),
			...commandLogsArray.map((log) => ({
				id: `command-${log.id}`,
				title: getActivityLogTitle(log),
				description: getTicketLogDescription(log),
				date: getDate(log),
				createdAt: log.createdAt,
			})),
		].sort((a, b) => {
			if (a.createdAt < b.createdAt) return 1;
			if (a.createdAt > b.createdAt) return -1;
			return 0;
		});
	}, [
		ticketLogs,
		commandLogs,
		getActivityLogTitle,
		getTicketLogTitle,
		getDate,
		getTicketLogDescription,
	]);

	if (!logs || logs.length === 0) {
		return null;
	}
	return (
		<Flex maxH="40vh">
			<Stepper
				flex={1}
				index={logs.length}
				orientation="vertical"
				gap="0"
				colorScheme="brand"
				overflow="auto"
				size={"xs"}
			>
				{logs.map((log) => (
					<Step key={log.id}>
						<StepIndicator></StepIndicator>
						<Box w="360px" h="80px">
							<Flex justifyContent="space-between">
								<Text
									lineHeight="21px"
									fontSize="14px"
									fontWeight="600"
									color="black"
								>
									{log.title}
								</Text>
								<Text
									lineHeight="20px"
									fontSize="14px"
									fontWeight="400"
									color="gray.600"
								>
									{log.date}
								</Text>
							</Flex>
							<Flex justifyContent="space-between">
								<Text
									lineHeight="20px"
									fontSize="14px"
									fontWeight="400"
									color="gray.600"
								>
									{log.description}
								</Text>
							</Flex>
						</Box>
						<StepSeparator />
					</Step>
				))}
			</Stepper>
		</Flex>
	);
}

function TicketingDetailDocuments({ id }: { id: number | string }) {
	const documents = useTicketDocuments(id);

	return (
		<Flex direction="column" gap={3}>
			{Array.isArray(documents) &&
				documents.map((document) => (
					<Document key={document.id} file={document}></Document>
				))}
		</Flex>
	);
}

function TicketingDetailPage() {
	const user = useConnectedUser();
	const { id } = useParams();
	const ticket = useTicket(id);
	const defaultRoom = useMemo(() => `ticket_${id}`, [id]);
	const parsedId = useMemo(() => (id ? Number.parseInt(id) : null), [id]);
	if (!ticket) {
		return <>{"Pas d'information sur ce ticket."}</>;
	}
	const patient = getTicketPatient(ticket);
	return (
		<Box h={"90vh"}>
			<Breadcrumb
				spacing="8px"
				separator={<ChevronRightIcon color="gray.500" />}
			>
				<BreadcrumbItem>
					<BreadcrumbLink
						href="/tickets"
						color="gray.600"
						fontWeight={600}
						fontSize="14px"
					>
						Ticketing
					</BreadcrumbLink>
				</BreadcrumbItem>

				<BreadcrumbItem>
					<BreadcrumbLink
						href="#"
						color="brand.700"
						fontWeight={600}
						fontSize="14px"
					>
						{format(ticket.createdAt, "yyyy")}-{id}
					</BreadcrumbLink>
				</BreadcrumbItem>
			</Breadcrumb>

			<div>
				<Box w={"400px"}>
					<Flex alignItems="center" gap={4} py={8}>
						<PageTitle display={"inline"}>
							{format(ticket.createdAt, "yyyy")}-{id}
						</PageTitle>
						<TicketStatusBadge
							id={ticket.id}
							editable={false}
						></TicketStatusBadge>
					</Flex>

					<Box>
						<Flex borderBottom="solid 1px" borderColor="gray.100" py={2}>
							<Text fontSize="14px" fontWeight="500" width={"200px"}>
								Motif
							</Text>
							<Text fontSize="14px">{ticket.ticketLabel.label}</Text>
						</Flex>
						<Flex borderBottom="solid 1px" borderColor="gray.100" py={2}>
							<Text fontSize="14px" fontWeight="500" width={"200px"}>
								Lié à
							</Text>
							<Flex fontSize="14px" gap={4}>
								<NavLink to={`/orders/${ticket.commandId}`}>
									<Text
										color={"brand.700"}
										fontWeight={500}
										textDecoration={"underline"}
										cursor={"pointer"}
									>
										{ticket?.command?.exposedId}
									</Text>
								</NavLink>
							</Flex>
						</Flex>
						<Flex borderBottom="solid 1px" borderColor="gray.100" py={2}>
							<Text fontSize="14px" fontWeight="500" width={"200px"}>
								Patient
							</Text>
							{patient.patientId ? (
								<NavLink to={`/patients/${patient.patientId}`}>
									<Text
										color={"brand.700"}
										fontWeight={500}
										textDecoration={"underline"}
										cursor={"pointer"}
									>
										{patient.name}
									</Text>
								</NavLink>
							) : (
								<Text fontSize="14px">{patient.name}</Text>
							)}
						</Flex>
						<Flex
							alignItems="center"
							borderBottom="solid 1px"
							borderColor="gray.100"
							py={2}
						>
							<Text fontSize="14px" fontWeight="500" width={"200px"}>
								Priorité
							</Text>
							<TicketPriorityBadge
								priority={ticket.priority}
							></TicketPriorityBadge>
						</Flex>
						{user?.isSuperAdmin && (
							<Flex
								alignItems="center"
								borderBottom="solid 1px"
								borderColor="gray.100"
								py={2}
							>
								<Text fontSize="14px" fontWeight="500" width={"200px"}>
									Statut commande
								</Text>
								<CommandStatusBadge
									id={ticket.commandId ?? null}
									editable={true}
								></CommandStatusBadge>
							</Flex>
						)}
						{user?.isSuperAdmin && (
							<Flex
								alignItems="center"
								borderBottom="solid 1px"
								borderColor="gray.100"
								py={2}
							>
								<Text fontSize="14px" fontWeight="500" width={"200px"}>
									Statut modifié par
								</Text>
								<CommandLastStatusChangeAuthor id={ticket.commandId ?? null} />
							</Flex>
						)}
						<Flex borderBottom="solid 1px" borderColor="gray.100" py={2}>
							<Text fontSize="14px" fontWeight="500" width={"200px"}>
								Ouvert par
							</Text>
							{ticket.openById && (
								<InlineUserAvatar
									id={ticket.openById}
									w="24px"
									h="24px"
								></InlineUserAvatar>
							)}
							{!ticket.openById && <Text>...</Text>}
						</Flex>
						<Flex borderBottom="solid 1px" borderColor="gray.100" py={2}>
							<Text fontSize="14px" fontWeight="500" width={"200px"}>
								Ouvert le
							</Text>
							<Text fontSize="14px" fontWeight="500">
								{ticket.createdAt
									? format(ticket.createdAt, "dd LLL yyyy à HH:mm")
									: "..."}
							</Text>
						</Flex>
						{ticket.status === TicketStatus.CLOSED && ticket.closedAt && (
							<Flex borderBottom="solid 1px" borderColor="gray.100" py={2}>
								<Text fontSize="14px" fontWeight="500" width={"200px"}>
									Fermé le
								</Text>
								<Text fontSize="14px" fontWeight="500">
									{format(ticket.closedAt, "dd LLL yyyy à HH:mm")}
								</Text>
							</Flex>
						)}
					</Box>

					<Box py={10}></Box>
				</Box>

				<PersistingTabIndex id="tickets">
					<TabList w="400px">
						<Tab
							_selected={{
								color: "brand.700",
								borderColor: "brand.700",
							}}
							w="50%"
						>
							Activité
						</Tab>
						<Tab
							_selected={{
								color: "brand.700",
								borderColor: "brand.700",
							}}
							w="50%"
						>
							Documents
						</Tab>
					</TabList>

					<TabPanels>
						<TabPanel px={0}>
							{id && (
								<TicketingDetailLogs
									id={id}
									commandId={ticket.commandId}
								></TicketingDetailLogs>
							)}
						</TabPanel>
						<TabPanel px={0}>
							{id && (
								<TicketingDetailDocuments id={id}></TicketingDetailDocuments>
							)}
						</TabPanel>
					</TabPanels>
				</PersistingTabIndex>
			</div>

			<Chat
				position={"absolute"}
				top={0}
				right={0}
				w={"calc(100vw - 280px - 480px)"}
				defaultRoom={defaultRoom}
				ticketId={parsedId}
			></Chat>
		</Box>
	);
}

export default TicketingDetailPage;
